/* ──────────────────────────────────────────────────────────────────────
 * LeadManagementCard  —  Section 1 of the redesigned Lead Actions panel
 *
 * The CRM-state-only surface. Lives at the top of the right-hand action
 * rail. Contains ONLY the four fields that describe what the lead "is"
 * in the pipeline today — never communication, never follow-ups.
 *
 *   Lead Status   (color-coded select)
 *   Priority      (colored indicator)
 *   Assignee      (team member select)
 *   VIP           (premium gold toggle)
 *
 * Behaviour: each control writes to the server instantly (PUT /leads/:id)
 * with optimistic UI. Lead-bus events propagate the change to every
 * other surface in the app (Timeline tab, drawer preview, dashboards,
 * pipeline kanban) without an extra refetch.
 *
 * Pairs visually with QuickActionsCard (Section 2) and
 * FollowUpManagementCard (Section 3) — together they replace the prior
 * monolithic "Lead Actions" panel and the legacy "Actions" Paper.
 * ──────────────────────────────────────────────────────────────────── */

import React, { useState } from "react";
import {
  Box, Typography, Stack, FormControl, InputLabel, Select, MenuItem, Button, Alert, Tooltip,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import {
  updateLead, STATUS_CONFIG, PRIORITY_CONFIG,
  type LeadStatus, type Priority, type LeadThread,
} from "./leadApi";

const NAVY = "#1a3353";
const GOLD = "#c9a84c";

interface TeamUserLike {
  id: number;
  display_name?: string | null;
  username?: string | null;
  first_name?: string | null;
  last_name?: string | null;
}

interface Props {
  lead: LeadThread;
  teamUsers: TeamUserLike[];
  onChanged?: (next: Partial<LeadThread>) => void;
  /** Read-only historical mode — non-admin previous assignee. Every control
   *  disables but the values stay visible so the audit trail reads cleanly. */
  readOnly?: boolean;
  /** Lock the Assignee dropdown specifically. True for every non-admin
   *  user even when they are the current owner — only Admin / Super Admin
   *  may change lead ownership. */
  assigneeLocked?: boolean;
}

export default function LeadManagementCard({
  lead, teamUsers, onChanged, readOnly = false, assigneeLocked = false,
}: Props) {
  const [error, setError] = useState<string | null>(null);
  // Per-field "saving" flag is intentionally NOT tracked — server writes
  // are quick enough that a spinner adds visual noise without operational
  // value. Errors surface inline below the controls.

  const persist = async (patch: Partial<LeadThread>) => {
    setError(null);
    try {
      await updateLead(lead.id, patch);
      onChanged?.(patch);
    } catch (e: any) {
      // eslint-disable-next-line no-console
      console.error("Lead update failed", e);
      setError(
        (typeof e?.message === "string" && e.message) ||
        "Couldn't save change. Please try again."
      );
    }
  };

  return (
    <Box>
      {/* ── Section header ─────────────────────────────────────── */}
      <Box mb={1.4}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: NAVY, letterSpacing: 0.2 }}>
          Lead Management
        </Typography>
        <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.1 }}>
          Controls the current sales pipeline stage of this lead.
        </Typography>
      </Box>

      <Stack gap={1.1}>
        {/* ── Status — color-coded select ──────────────────────── */}
        <FormControl size="small" fullWidth disabled={readOnly}>
          <InputLabel>Lead Status</InputLabel>
          <Select
            value={lead.status}
            label="Lead Status"
            disabled={readOnly}
            onChange={(e) => persist({ status: e.target.value as LeadStatus })}
            renderValue={(v) => {
              const cfg = STATUS_CONFIG[v as LeadStatus];
              return (
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: cfg?.color }} />
                  <Box component="span" sx={{ fontWeight: 700 }}>{cfg?.label ?? v}</Box>
                </Stack>
              );
            }}
          >
            {Object.entries(STATUS_CONFIG).map(([k, v]) => (
              <MenuItem key={k} value={k}>
                <Stack direction="row" alignItems="center" gap={1}>
                  <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: v.color }} />
                  {v.label}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* ── Priority + Assignee — side-by-side ───────────────── */}
        <Stack direction="row" gap={1}>
          <FormControl size="small" sx={{ flex: 1 }} disabled={readOnly}>
            <InputLabel>Priority</InputLabel>
            <Select
              value={lead.priority || "medium"}
              label="Priority"
              disabled={readOnly}
              onChange={(e) => persist({ priority: e.target.value as Priority })}
              renderValue={(v) => {
                const cfg = PRIORITY_CONFIG[v as Priority];
                return (
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: cfg?.color }} />
                    <Box component="span">{cfg?.label ?? v}</Box>
                  </Stack>
                );
              }}
            >
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                <MenuItem key={k} value={k}>
                  <Stack direction="row" alignItems="center" gap={1}>
                    <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: v.color }} />
                    {v.label}
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Assignee — disabled for every non-admin (assigneeLocked) and
              for read-only previous assignees. Tooltip surfaces the
              "Only administrators can change lead ownership." copy from
              the spec. The lock icon adornment makes the restriction
              obvious without a click. */}
          <Tooltip
            title={
              assigneeLocked || readOnly
                ? "Only administrators can change lead ownership."
                : ""
            }
            placement="top"
            arrow
            disableHoverListener={!(assigneeLocked || readOnly)}
          >
            <FormControl
              size="small"
              sx={{ flex: 1 }}
              disabled={assigneeLocked || readOnly}
            >
              <InputLabel>Assignee</InputLabel>
              <Select
                value={(lead.assigned_to ?? "") as number | ""}
                label="Assignee"
                disabled={assigneeLocked || readOnly}
                onChange={(e) =>
                  persist({
                    assigned_to: e.target.value === "" ? null : Number(e.target.value),
                  } as Partial<LeadThread>)
                }
                endAdornment={
                  (assigneeLocked || readOnly) ? (
                    <LockOutlinedIcon
                      sx={{ fontSize: 15, color: "#94a3b8", mr: 3 }}
                    />
                  ) : undefined
                }
              >
                <MenuItem value="">Unassigned</MenuItem>
                {teamUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>
                    {u.display_name
                      || [u.first_name, u.last_name].filter(Boolean).join(" ")
                      || u.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Tooltip>
        </Stack>

        {/* ── VIP toggle — premium gold button ─────────────────── */}
        <Button
          fullWidth
          size="small"
          variant={lead.is_vip ? "contained" : "outlined"}
          disabled={readOnly}
          startIcon={lead.is_vip
            ? <StarIcon sx={{ fontSize: 16 }} />
            : <StarBorderIcon sx={{ fontSize: 16 }} />}
          onClick={() => persist({ is_vip: !lead.is_vip })}
          sx={{
            textTransform: "none", fontSize: 12, fontWeight: 700,
            borderRadius: "8px", py: 0.7, letterSpacing: 0.3,
            bgcolor: lead.is_vip ? GOLD : "transparent",
            color: lead.is_vip ? NAVY : GOLD,
            borderColor: GOLD,
            borderWidth: 1.5,
            "&:hover": {
              bgcolor: lead.is_vip ? "#b8943f" : "rgba(201,168,76,0.1)",
              borderColor: GOLD,
              borderWidth: 1.5,
            },
          }}
        >
          {lead.is_vip ? "VIP Client" : "Mark as VIP"}
        </Button>

        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ fontSize: 11.5, py: 0.3, borderRadius: 1.5 }}>
            {error}
          </Alert>
        )}
      </Stack>
    </Box>
  );
}
